import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, TextField, InputAdornment } from '@mui/material';
import { campaignService } from '../services/campaignService';
import { Campaign, Prize, Sponsor, Store as StoreType } from '../types';
import { CampaignImage } from '../components/CampaignImage';
import { Icon } from '../components/participant/Icon';
import { ParticipantTopBar } from '../components/participant/ParticipantTopBar';
import { BottomSheet } from '../components/participant/BottomSheet';
import { participantColors as c, fadeUpKeyframes } from '../theme/participantTheme';

const formatDate = (dateString: string): string => new Date(dateString).toLocaleDateString('pt-BR');

const PRIZE_ICONS = ['redeem', 'emoji_events', 'two_wheeler', 'tv'];

const UserCampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState<'regulation' | 'stores' | 'sponsors' | null>(null);
  const [storeSearch, setStoreSearch] = useState('');

  useEffect(() => {
    const fetchCampaignDetails = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const campaignData = await campaignService.getCampaignById(id);
        setCampaign(campaignData);
        setPrizes(campaignData.prizes || []);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Erro ao carregar detalhes da campanha');
      } finally {
        setLoading(false);
      }
    };
    fetchCampaignDetails();
  }, [id]);

  const filteredStores = (campaign?.stores || [])
    .filter((store) => {
      if (!storeSearch.trim()) return true;
      const q = storeSearch.toLowerCase().trim();
      return store.name.toLowerCase().includes(q) || store.address?.toLowerCase().includes(q);
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: c.gradients.pageBg }}>
        <CircularProgress sx={{ color: c.brandMid }} size={48} />
      </Box>
    );
  }

  if (error || !campaign) {
    return (
      <Box sx={{ minHeight: '100vh', background: c.gradients.pageBg, fontFamily: c.fontFamily, p: 2 }}>
        <Box sx={{ maxWidth: 620, mx: 'auto', display: 'flex', flexDirection: 'column', gap: '16px', pt: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: c.errorBg,
              border: `2px solid ${c.errorBorder}`,
              borderRadius: `${c.radius.input}px`,
              padding: '13px 15px',
            }}
          >
            <Icon name="error" size={22} color={c.error} />
            <Box component="span" sx={{ fontSize: 15, fontWeight: 700, color: c.errorTextDark }}>
              {error || 'Campanha não encontrada'}
            </Box>
          </Box>
          <Box
            component="button"
            onClick={() => navigate('/user/campaigns')}
            sx={{
              minHeight: 58,
              border: `2px solid ${c.borderHover}`,
              borderRadius: `${c.radius.button}px`,
              background: c.surface,
              color: c.brandDark,
              fontFamily: c.fontFamily,
              fontSize: 16,
              fontWeight: 800,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <Icon name="arrow_back" size={20} />
            Voltar para campanhas
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: c.gradients.pageBg, fontFamily: c.fontFamily, pb: '48px' }}>
      <style>{fadeUpKeyframes}</style>

      <Box sx={{ maxWidth: 620, mx: 'auto', px: 2, display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <ParticipantTopBar />

        {/* Hero */}
        <Box sx={{ background: c.surface, borderRadius: `${c.radius.cardLarge}px`, boxShadow: c.shadows.card, overflow: 'hidden', animation: 'participantFadeUp 0.35s ease both' }}>
          <Box sx={{ position: 'relative' }}>
            <CampaignImage imageUrl={campaign.imageUrl} alt={campaign.name} width="100%" height={220} borderRadius={0} objectFit="cover" />
            <Box
              component="button"
              onClick={() => navigate('/user/campaigns')}
              title="Voltar"
              sx={{
                position: 'absolute',
                top: 14,
                left: 14,
                width: 48,
                height: 48,
                border: 'none',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.94)',
                color: c.brandDark,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
              }}
            >
              <Icon name="arrow_back" size={24} />
            </Box>
            {campaign.status === 'ACTIVE' && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 14,
                  right: 14,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  background: c.brandMid,
                  color: c.onDarkPrimary,
                  borderRadius: `${c.radius.pill}px`,
                  padding: '7px 14px',
                  fontSize: 13.5,
                  fontWeight: 800,
                }}
              >
                <Icon name="radio_button_checked" size={16} />
                ATIVA
              </Box>
            )}
          </Box>

          <Box sx={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Box component="div" sx={{ fontSize: 24, fontWeight: 800, color: c.textDarkest }}>
              {campaign.name}
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                background: c.surfaceDark,
                color: c.onDarkPrimary,
                borderRadius: `${c.radius.tile}px`,
                padding: '13px 16px',
              }}
            >
              <Icon name="calendar_month" size={20} color={c.onDarkAccent} />
              <Box component="span" sx={{ fontSize: 14.5, fontWeight: 700 }}>
                {formatDate(campaign.startDate)} até {formatDate(campaign.endDate)}
              </Box>
            </Box>

            {campaign.description && (
              <Box component="div" sx={{ fontSize: 15.5, color: c.textSecondary, lineHeight: 1.6 }}>
                {campaign.description}
              </Box>
            )}
          </Box>
        </Box>

        {/* Prêmios */}
        <Box sx={{ background: c.surface, borderRadius: `${c.radius.cardLarge}px`, padding: '22px', boxShadow: c.shadows.card, animation: 'participantFadeUp 0.4s ease both' }}>
          <Box component="div" sx={{ fontSize: 19, fontWeight: 800, color: c.textDarkest, mb: '14px' }}>
            Prêmios
          </Box>
          {prizes.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {prizes.map((prize, i) => (
                <Box
                  key={prize.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    background: c.amberBg,
                    borderRadius: `${c.radius.tile}px`,
                    padding: '12px 14px',
                  }}
                >
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      flexShrink: 0,
                      borderRadius: `${c.radius.tile}px`,
                      background: c.amber,
                      color: c.onDarkPrimary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon name={PRIZE_ICONS[i % PRIZE_ICONS.length]} size={26} />
                  </Box>
                  <Box>
                    <Box component="div" sx={{ fontSize: 17, fontWeight: 800, color: c.amberTextDarker }}>
                      {prize.name}
                    </Box>
                    <Box component="div" sx={{ fontSize: 14, fontWeight: 600, color: c.amberTextDark }}>
                      Quantidade: {prize.quantity}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box component="div" sx={{ fontSize: 15, color: c.textTertiary }}>
              Nenhum prêmio cadastrado para esta campanha.
            </Box>
          )}
        </Box>

        {/* CTA primário */}
        <Box
          component="button"
          onClick={() => navigate(`/my-coupons/new/${campaign.id}`)}
          sx={{
            width: '100%',
            minHeight: 62,
            border: 'none',
            borderRadius: `${c.radius.button}px`,
            background: c.gradients.primaryButton,
            color: c.onDarkPrimary,
            fontFamily: c.fontFamily,
            fontSize: 19,
            fontWeight: 800,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            boxShadow: c.shadows.primaryButton,
            transition: 'transform 0.15s',
            animation: 'participantFadeUp 0.45s ease both',
            '&:hover': { transform: 'translateY(-2px)' },
          }}
        >
          <Icon name="confirmation_number" size={24} />
          Cadastrar meu cupom
        </Box>

        {/* Como participar */}
        <Box sx={{ background: c.surface, borderRadius: `${c.radius.cardLarge}px`, padding: '22px', boxShadow: c.shadows.card, animation: 'participantFadeUp 0.5s ease both' }}>
          <Box component="div" sx={{ fontSize: 19, fontWeight: 800, color: c.textDarkest, mb: '16px' }}>
            Como participar
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {[
              'Faça suas compras em uma das lojas participantes desta campanha',
              'Cadastre o número do seu cupom fiscal aqui no sistema',
              'Aguarde o sorteio e concorra aos prêmios incríveis!',
            ].map((step, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    borderRadius: '50%',
                    background: c.surfaceTint,
                    color: c.brandDark,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 17,
                    fontWeight: 800,
                  }}
                >
                  {i + 1}
                </Box>
                <Box component="div" sx={{ fontSize: 16, color: c.textSecondary, lineHeight: 1.5, pt: '6px' }}>
                  {step}
                </Box>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: c.amberBg,
              borderRadius: `${c.radius.tile}px`,
              padding: '13px 15px',
              mt: '16px',
            }}
          >
            <Icon name="lightbulb" size={22} color={c.amber} />
            <Box component="span" sx={{ fontSize: 14.5, fontWeight: 700, color: c.amberTextDark }}>
              Quanto mais cupons você cadastrar, maiores suas chances de ganhar!
            </Box>
          </Box>
        </Box>

        {/* Info buttons */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '12px',
            animation: 'participantFadeUp 0.55s ease both',
          }}
        >
          {[
            { key: 'regulation' as const, label: 'Regulamento', icon: 'description' },
            { key: 'stores' as const, label: 'Lojas', icon: 'storefront' },
            { key: 'sponsors' as const, label: 'Patrocinadores', icon: 'handshake' },
          ].map((btn) => (
            <Box
              key={btn.key}
              component="button"
              onClick={() => setModal(btn.key)}
              sx={{
                minHeight: 58,
                border: `2px solid ${c.borderHover}`,
                borderRadius: `${c.radius.button}px`,
                background: c.surface,
                color: c.brandDark,
                fontFamily: c.fontFamily,
                fontSize: 15.5,
                fontWeight: 800,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                '&:hover': { background: c.surfaceHover },
              }}
            >
              <Icon name={btn.icon} size={20} />
              {btn.label}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Modal: Regulamento / Lojas / Patrocinadores */}
      <BottomSheet open={modal !== null} onClose={() => { setModal(null); setStoreSearch(''); }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: `1px solid ${c.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icon
              name={modal === 'regulation' ? 'description' : modal === 'stores' ? 'storefront' : 'handshake'}
              size={24}
              color={c.brandDark}
            />
            <Box component="div" sx={{ fontSize: 19, fontWeight: 800, color: c.textDarkest }}>
              {modal === 'regulation' ? 'Regulamento' : modal === 'stores' ? 'Lojas participantes' : 'Patrocinadores'}
            </Box>
          </Box>
          <Box
            component="button"
            onClick={() => { setModal(null); setStoreSearch(''); }}
            sx={{
              width: 46,
              height: 46,
              border: `2px solid ${c.border}`,
              borderRadius: `${c.radius.tile}px`,
              background: c.surfaceHover,
              color: c.brandIcon,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="close" size={22} />
          </Box>
        </Box>

        <Box sx={{ overflow: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {modal === 'regulation' &&
            (campaign.regulation ? (
              <Box component="div" sx={{ fontSize: 15, color: c.textSecondary, whiteSpace: 'pre-line', lineHeight: 1.7 }}>
                {campaign.regulation}
              </Box>
            ) : (
              <Box component="div" sx={{ fontSize: 15, color: c.textTertiary, textAlign: 'center', py: 3 }}>
                Regulamento não disponível para esta campanha.
              </Box>
            ))}

          {modal === 'stores' && (
            <>
              <TextField
                fullWidth
                placeholder="Buscar loja pelo nome ou endereço..."
                value={storeSearch}
                onChange={(e) => setStoreSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon name="search" size={20} color={c.brandIcon} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: `${c.radius.input}px`, fontFamily: c.fontFamily },
                  mb: '4px',
                }}
              />
              {filteredStores.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Icon name="search_off" size={40} color={c.iconMuted} />
                  <Box component="div" sx={{ fontSize: 15, color: c.textTertiary, mt: '8px' }}>
                    Nenhuma loja encontrada
                  </Box>
                </Box>
              ) : (
                filteredStores.map((store: StoreType) => (
                  <Box
                    key={store.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      background: '#fbfdfb',
                      border: `1px solid ${c.borderModalRow}`,
                      borderRadius: `${c.radius.tile}px`,
                      padding: '12px 14px',
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        flexShrink: 0,
                        borderRadius: `${c.radius.tile}px`,
                        background: c.surfaceTint,
                        color: c.brandDark,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon name="storefront" size={22} />
                    </Box>
                    <Box>
                      <Box component="div" sx={{ fontSize: 15.5, fontWeight: 700, color: c.textDarkest }}>
                        {store.name}
                      </Box>
                      {store.address && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '2px' }}>
                          <Icon name="location_on" size={15} color={c.textTertiary} />
                          <Box component="span" sx={{ fontSize: 13.5, color: c.textTertiary }}>
                            {store.address}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))
              )}
            </>
          )}

          {modal === 'sponsors' &&
            (campaign.sponsors && campaign.sponsors.length > 0 ? (
              campaign.sponsors.map((sponsor: Sponsor) => (
                <Box
                  key={sponsor.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    background: '#fbfdfb',
                    border: `1px solid ${c.borderModalRow}`,
                    borderRadius: `${c.radius.tile}px`,
                    padding: '12px 14px',
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      flexShrink: 0,
                      borderRadius: `${c.radius.tile}px`,
                      background: c.surfaceTint,
                      color: c.brandDark,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon name="handshake" size={22} />
                  </Box>
                  <Box component="div" sx={{ fontSize: 15.5, fontWeight: 700, color: c.textDarkest }}>
                    {sponsor.name}
                  </Box>
                </Box>
              ))
            ) : (
              <Box component="div" sx={{ fontSize: 15, color: c.textTertiary, textAlign: 'center', py: 3 }}>
                Nenhum patrocinador cadastrado para esta campanha.
              </Box>
            ))}
        </Box>
      </BottomSheet>
    </Box>
  );
};

export default UserCampaignDetail;
